import { EntryType, logStatEvent } from "../hooks/logStats";

export type PromoSide = "left" | "right";

type Attribution = {
  clickId: string;
  clickedAt: string;
  visitorId: string;
  side: PromoSide;
};

const STORAGE_KEY = "promo-signup-attribution-v1";
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function readAttributions(): Partial<Record<PromoSide, Attribution>> {
  if (typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

function makeClickId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isRecent(attribution?: Attribution): attribution is Attribution {
  if (!attribution?.clickId || !attribution.clickedAt) return false;
  const clickedAt = Date.parse(attribution.clickedAt);
  return Number.isFinite(clickedAt) && Date.now() - clickedAt <= ATTRIBUTION_WINDOW_MS;
}

export function recordPromoClick(
  side: PromoSide,
  userId: string | null,
  isGuest: boolean,
  deviceType: "desktop" | "mobile",
): void {
  const attributions = readAttributions();
  let attribution = attributions[side];
  if (!isRecent(attribution)) {
    attribution = {
      clickId: makeClickId(),
      clickedAt: new Date().toISOString(),
      visitorId: userId || "",
      side,
    };
  }
  attributions[side] = attribution;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attributions));
  } catch {
    // Stats still record the click if storage is unavailable.
  }

  if (userId) {
    const entryType: EntryType = side === "left" ? "leftPromoBarClick" : "rightPromoBarClick";
    void logStatEvent(userId, entryType, {
      searchWordId: attribution.clickId,
      deviceType,
      userType: isGuest ? "guest" : "user",
    });
  }
}

export function recordPromoSignup(userId: string): void {
  if (!userId || typeof window === "undefined") return;
  const attributions = readAttributions();
  Object.values(attributions).forEach((attribution) => {
    if (!isRecent(attribution)) return;
    const entryType: EntryType = attribution.side === "left" ? "leftPromoBarSignup" : "rightPromoBarSignup";
    void logStatEvent(userId, entryType, {
      searchWordId: attribution.clickId,
      deviceType: "desktop",
      userType: "user",
    });
  });
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Conversion stats are already sent; storage cleanup is best-effort.
  }
}
